import React, { useState, useEffect, CSSProperties } from 'react';
import { requestPasswordReset, resetUserPasswordToken } from '../adapters/authentication';

const Icon = ({ d, size = 16 }: { d: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

// ─── Shared layout shell ─────────────────────────────────────────────────────

function ResetShell({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState(() => localStorage.getItem('pop_theme') || 'light');
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('pop_theme', theme);
  }, [theme]);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-app)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }}>
      <button
        onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}
        title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
        style={{
          position: 'fixed', top: 20, right: 20, zIndex: 10,
          width: 36, height: 36, borderRadius: 10,
          background: 'var(--bg-surface)', border: '1px solid var(--border-soft)',
          color: 'var(--fg-default)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', boxShadow: 'var(--shadow-card)',
        }}
      >
        {theme === 'dark'
          ? <Icon d="M12 3v1M12 20v1M4.2 4.2l.7.7M19.1 19.1l.7.7M3 12h1M20 12h1M4.2 19.8l.7-.7M19.1 4.9l.7-.7M12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10z" size={16} />
          : <Icon d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" size={16} />}
      </button>

      <div style={{
        width: '100%', maxWidth: 480,
        background: 'var(--bg-surface)',
        borderRadius: 18, overflow: 'hidden',
        boxShadow: '0 30px 80px rgba(15,23,42,0.18), 0 4px 12px rgba(15,23,42,0.06)',
        border: '1px solid var(--border-subtle)',
      }}>
        {/* Header bar */}
        <div style={{
          padding: '20px 32px',
          background: 'linear-gradient(135deg, #0c1b33 0%, #0f2545 100%)',
          display: 'flex', alignItems: 'center', gap: 12,
          borderBottom: '1px solid rgba(255,255,255,0.07)',
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: 9,
            background: 'linear-gradient(135deg, #00c896, #009B4D)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 16, fontWeight: 800, color: '#0c1b33',
          }}>S</div>
          <div style={{ fontWeight: 800, fontSize: 16, letterSpacing: '-0.02em', color: '#fff' }}>
            smartstreet<span style={{ color: '#00c896' }}>.ai</span>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: '36px 32px', background: 'var(--bg-surface-2)' }}>
          {children}
        </div>
      </div>
    </div>
  );
}

// ─── Shared style helpers ────────────────────────────────────────────────────

function lbl(): CSSProperties {
  return {
    display: 'block', fontSize: 11, fontWeight: 700,
    letterSpacing: '0.06em', textTransform: 'uppercase',
    color: 'var(--fg-muted)', marginBottom: 6,
  };
}

function inputStyle(): CSSProperties {
  return {
    width: '100%', padding: '10px 12px',
    background: 'var(--bg-surface-3)',
    border: '1px solid var(--border-strong)',
    borderRadius: 8, fontSize: 13, color: 'var(--fg-strong)',
    fontFamily: 'inherit', outline: 'none',
    transition: 'border-color 0.15s', boxSizing: 'border-box',
  };
}

function primaryBtn(disabled: boolean): CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    width: '100%', padding: '12px 18px',
    background: disabled ? 'var(--bg-surface-3)' : 'var(--brand-navy-800)',
    color: disabled ? 'var(--fg-faint)' : '#fff',
    border: 'none', borderRadius: 10,
    fontSize: 13.5, fontWeight: 700,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontFamily: 'inherit', marginTop: 20,
  };
}

function Alert({ type, children }: { type: 'error' | 'success'; children: React.ReactNode }) {
  const isError = type === 'error';
  return (
    <div style={{
      marginTop: 14, padding: '10px 14px', borderRadius: 8, fontSize: 12.5,
      background: isError ? 'rgba(220,38,38,0.08)' : 'rgba(0,200,150,0.08)',
      border: `1px solid ${isError ? 'rgba(220,38,38,0.25)' : 'rgba(0,200,150,0.25)'}`,
      color: isError ? '#dc2626' : 'var(--brand-green-500)',
    }}>{children}</div>
  );
}

// ─── Forgot password (request reset) ────────────────────────────────────────

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      const msg = await requestPasswordReset(email);
      setSuccess(msg);
    } catch (err: any) {
      setError(
        err?.detail ??
        err?.email?.[0] ??
        'Unable to send reset link. Please check the email and try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <ResetShell>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{
          fontSize: 24, fontWeight: 700, color: 'var(--fg-strong)',
          letterSpacing: '-0.02em', marginBottom: 6, lineHeight: 1.2,
        }}>Reset your password</h2>
        <p style={{ fontSize: 13, color: 'var(--fg-muted)', lineHeight: 1.5 }}>
          Enter your work email and we'll send you a link to reset your password.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div>
          <label style={lbl()}>Work email</label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="you@company.com"
            required
            style={inputStyle()}
          />
        </div>

        {error && <Alert type="error">{error}</Alert>}
        {success && <Alert type="success">{success}</Alert>}

        <button type="submit" disabled={loading} style={primaryBtn(loading)}>
          {loading ? 'Sending…' : 'Send reset link'}
          {!loading && <Icon d="M5 12h14M13 5l7 7-7 7" size={14} />}
        </button>
      </form>

      <div style={{ marginTop: 20, textAlign: 'center', fontSize: 12, color: 'var(--fg-muted)' }}>
        <a href="/login" style={{ color: 'var(--brand-green-500)', fontWeight: 700, textDecoration: 'none' }}>
          ← Back to sign in
        </a>
      </div>
    </ResetShell>
  );
}

// ─── Reset password with token ───────────────────────────────────────────────

export function ResetPassword() {
  // Support both /reset-password/TOKEN and /reset-password?token=TOKEN
  const tokenFromPath = window.location.pathname.replace(/^\/reset-password\/?/, '') || null;
  const tokenFromQuery = new URLSearchParams(window.location.search).get('token');
  const token = tokenFromPath || tokenFromQuery;

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (!token) { setError('Invalid or missing reset token.'); return; }
    setLoading(true);
    try {
      const msg = await resetUserPasswordToken(token, password);
      setSuccess(msg);
      setTimeout(() => { window.location.replace('/login'); }, 1500);
    } catch (err: any) {
      setError(
        err?.detail ??
        err?.password?.[0] ??
        'Unable to reset password. The link may have expired.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <ResetShell>
        <Alert type="error">Invalid or missing reset token. Please request a new password reset link.</Alert>
        <div style={{ marginTop: 20, textAlign: 'center', fontSize: 12 }}>
          <a href="/forgot-password" style={{ color: 'var(--brand-green-500)', fontWeight: 700, textDecoration: 'none' }}>
            Request a new link
          </a>
        </div>
      </ResetShell>
    );
  }

  return (
    <ResetShell>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{
          fontSize: 24, fontWeight: 700, color: 'var(--fg-strong)',
          letterSpacing: '-0.02em', marginBottom: 6, lineHeight: 1.2,
        }}>Set a new password</h2>
        <p style={{ fontSize: 13, color: 'var(--fg-muted)', lineHeight: 1.5 }}>
          Choose a strong password for your account.
        </p>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label style={lbl()}>New password</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPw ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="At least 10 characters"
              required
              style={{ ...inputStyle(), paddingRight: 38 }}
            />
            <button type="button" onClick={() => setShowPw(p => !p)}
              style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                background: 'transparent', border: 'none',
                color: 'var(--fg-muted)', padding: 6, cursor: 'pointer',
                display: 'flex', alignItems: 'center',
              }}
              title={showPw ? 'Hide' : 'Show'}
            >
              {showPw
                ? <Icon d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7zM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" size={15} />
                : <Icon d="M2 2l20 20M9.9 4.2A10 10 0 0 1 12 4c6.5 0 10 7 10 7a18 18 0 0 1-3.4 4.4M6.5 6.5A18 18 0 0 0 2 11s3.5 7 10 7c1.4 0 2.7-.3 3.9-.8" size={15} />}
            </button>
          </div>
        </div>

        <div>
          <label style={lbl()}>Confirm new password</label>
          <input
            type={showPw ? 'text' : 'password'}
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            placeholder="Repeat your password"
            required
            style={inputStyle()}
          />
        </div>

        {error && <Alert type="error">{error}</Alert>}
        {success && <Alert type="success">{success} Redirecting to sign in…</Alert>}

        <button type="submit" disabled={loading} style={primaryBtn(loading)}>
          {loading ? 'Resetting…' : 'Reset password'}
          {!loading && <Icon d="M5 12h14M13 5l7 7-7 7" size={14} />}
        </button>
      </form>

      <div style={{ marginTop: 20, textAlign: 'center', fontSize: 12, color: 'var(--fg-muted)' }}>
        <a href="/login" style={{ color: 'var(--brand-green-500)', fontWeight: 700, textDecoration: 'none' }}>
          ← Back to sign in
        </a>
      </div>
    </ResetShell>
  );
}
